/**
 * Application constants
 */

import { UserRole } from "../types/auth.types";
import { Status } from "../types/common.types";
import { TaskStatus, TaskPriority } from "../types/task.types";
import { EventStatus, EventType } from "../types/event.types";
import {
  InteractionType,
  InteractionDirection,
  InteractionOutcome,
} from "../types/interaction.types";

// Application metadata
export const APP_NAME = "USLS Relevate";
export const APP_VERSION = "1.0.0";
export const APP_DESCRIPTION = "Customer Relationship Management System";

// API Configuration
export const API_TIMEOUT = 30000; // 30 seconds
export const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// Pagination defaults
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// User roles
export const USER_ROLES = [
  { value: UserRole.SUPER_ADMIN, label: "Super Admin" },
  { value: UserRole.ADMIN, label: "Admin" },
  { value: UserRole.MANAGER, label: "Manager" },
  { value: UserRole.REGIONAL_MANAGER, label: "Regional Manager" },
  { value: UserRole.TERRITORY_MANAGER, label: "Territory Manager" },
  { value: UserRole.STAFF, label: "Staff" },
  { value: UserRole.PARTNER, label: "Partner" },
  { value: UserRole.FINANCE, label: "Finance" },
  { value: UserRole.READONLY, label: "Read Only" },
  { value: UserRole.SUPPORT_ADMIN, label: "Support Admin" },
  { value: UserRole.SUPPORT_AGENT, label: "Support Agent" },
  { value: UserRole.IT, label: "IT" },
  // Invitable while the feature is dark ON PURPOSE: attorneys and their state
  // coverage must exist BEFORE routing can be activated, exactly as coverage config
  // is ungated server-side. The dark flag hides the question surface, not setup.
  { value: UserRole.ATTORNEY, label: "Attorney" },
];

// Status options
export const STATUS_OPTIONS = [
  { value: Status.ACTIVE, label: "Active", color: "green" },
  { value: Status.INACTIVE, label: "Inactive", color: "gray" },
  { value: Status.PENDING, label: "Pending", color: "yellow" },
  { value: Status.ARCHIVED, label: "Archived", color: "red" },
];

// Task statuses
export const TASK_STATUSES = [
  { value: TaskStatus.TODO, label: "To Do", color: "gray" },
  { value: TaskStatus.IN_PROGRESS, label: "In Progress", color: "blue" },
  { value: TaskStatus.BLOCKED, label: "Blocked", color: "yellow" },
  { value: TaskStatus.COMPLETED, label: "Completed", color: "green" },
  { value: TaskStatus.CANCELLED, label: "Cancelled", color: "red" },
];

// Task priorities (tasks.priority is an INTEGER 1-5; higher = more urgent)
export const TASK_PRIORITIES = [
  { value: TaskPriority.LOWEST, label: "Lowest", color: "gray" },
  { value: TaskPriority.LOW, label: "Low", color: "gray" },
  { value: TaskPriority.MEDIUM, label: "Medium", color: "blue" },
  { value: TaskPriority.HIGH, label: "High", color: "orange" },
  { value: TaskPriority.URGENT, label: "Urgent", color: "red" },
];

// Event statuses
export const EVENT_STATUSES = [
  { value: EventStatus.SCHEDULED, label: "Scheduled", color: "blue" },
  { value: EventStatus.IN_PROGRESS, label: "In Progress", color: "yellow" },
  { value: EventStatus.COMPLETED, label: "Completed", color: "green" },
  { value: EventStatus.CANCELLED, label: "Cancelled", color: "red" },
  { value: EventStatus.POSTPONED, label: "Postponed", color: "orange" },
];

// Event types — FULL vocabulary (current + legacy + new). Used by list
// filters, detail displays, and historical event rendering so legacy
// values still resolve. Create/edit dropdowns should use
// VISIBLE_EVENT_TYPES below.
export const EVENT_TYPES = [
  { value: EventType.CLASS, label: "Class", icon: "academic-cap" },
  { value: EventType.TABLE, label: "Table", icon: "table-cells" },
  { value: EventType.GUN_SHOW, label: "Gun Show", icon: "building-storefront" },
  // Smart Admin replacement R1 (2026-05-17) — new types
  {
    value: EventType.SPEAKING_ENGAGEMENT,
    label: "Speaking Engagement",
    icon: "microphone",
  },
  {
    value: EventType.SEMINAR,
    label: "Seminar / Workshop / GSW",
    icon: "presentation-chart-bar",
  },
  {
    value: EventType.CONFERENCE,
    label: "Conference / Expo / Trade Show",
    icon: "building",
  },
  {
    value: EventType.NETWORKING_EVENT,
    label: "Networking Event",
    icon: "user-group",
  },
  // Classification-driven create flow (2026-07-28) — conversion=Yes/registration=No.
  {
    value: EventType.MEMBER_CHECK_IN,
    label: "Member Check-in",
    icon: "identification",
  },
  {
    value: EventType.LIGHTNING_DEALS,
    label: "Lightning Deals",
    icon: "bolt",
  },
  // Range Night (2026-07-31) — member-facing conversion + registration ("full box").
  {
    value: EventType.RANGE_NIGHT,
    label: "Range Night",
    icon: "fire",
  },
  // Deprecated — hidden in create/edit dropdowns via VISIBLE_EVENT_TYPES,
  // but retained here so existing/historical events keep their labels in
  // the list view, filter dropdowns, and detail pages.
  { value: EventType.SHOW, label: "Show", icon: "building" },
  {
    value: EventType.PRIVATE_EVENT,
    label: "Private Event",
    icon: "lock-closed",
  },
  {
    value: EventType.COMMUNITY_EVENT,
    label: "Community Event",
    icon: "user-group",
  },
  { value: EventType.MEETING, label: "Meeting", icon: "users" },
  { value: EventType.TRAINING, label: "Training", icon: "academic-cap" },
  { value: EventType.WEBINAR, label: "Webinar", icon: "video-camera" },
  { value: EventType.CALL, label: "Call", icon: "phone" },
  { value: EventType.SOCIAL, label: "Social", icon: "cake" },
  { value: EventType.OTHER, label: "Other", icon: "calendar" },
];

// Event types visible in create/edit dropdowns. Subset of EVENT_TYPES
// per Heather's R1 requirements (page 1 of the requirements PDF: 8
// types in / 9 types out). Hides deprecated types from new event
// creation while keeping them valid for historical data.
export const VISIBLE_EVENT_TYPE_VALUES: ReadonlySet<string> = new Set([
  EventType.CLASS,
  EventType.TABLE,
  EventType.GUN_SHOW,
  EventType.SPEAKING_ENGAGEMENT,
  EventType.SEMINAR,
  EventType.CONFERENCE,
  EventType.NETWORKING_EVENT,
  EventType.MEMBER_CHECK_IN,
  EventType.LIGHTNING_DEALS,
  EventType.RANGE_NIGHT,
  // NOTE: phone_sale intentionally excluded — per the R0 audit verdict
  // it belongs in a future sales_activities model.
]);

export const VISIBLE_EVENT_TYPES = EVENT_TYPES.filter((t) =>
  VISIBLE_EVENT_TYPE_VALUES.has(t.value),
);

// Interaction types
export const INTERACTION_TYPES = [
  { value: InteractionType.EMAIL, label: "Email", icon: "mail" },
  { value: InteractionType.PHONE_CALL, label: "Phone Call", icon: "phone" },
  { value: InteractionType.MEETING, label: "Meeting", icon: "users" },
  { value: InteractionType.NOTE, label: "Note", icon: "document-text" },
  { value: InteractionType.SMS, label: "SMS", icon: "chat-bubble-left" },
  { value: InteractionType.SOCIAL_MEDIA, label: "Social Media", icon: "share" },
  {
    value: InteractionType.CHAT,
    label: "Chat",
    icon: "chat-bubble-left-right",
  },
  {
    value: InteractionType.FORM_SUBMISSION,
    label: "Form Submission",
    icon: "clipboard-document",
  },
  {
    value: InteractionType.DOCUMENT_SHARED,
    label: "Document Shared",
    icon: "document-duplicate",
  },
  { value: InteractionType.OTHER, label: "Other", icon: "ellipsis-horizontal" },
];

// Interaction directions
export const INTERACTION_DIRECTIONS = [
  {
    value: InteractionDirection.INBOUND,
    label: "Inbound",
    icon: "arrow-down-circle",
  },
  {
    value: InteractionDirection.OUTBOUND,
    label: "Outbound",
    icon: "arrow-up-circle",
  },
];

// Interaction outcomes
export const INTERACTION_OUTCOMES = [
  { value: InteractionOutcome.SUCCESSFUL, label: "Successful", color: "green" },
  { value: InteractionOutcome.NO_ANSWER, label: "No Answer", color: "yellow" },
  {
    value: InteractionOutcome.LEFT_MESSAGE,
    label: "Left Message",
    color: "blue",
  },
  {
    value: InteractionOutcome.FOLLOW_UP_NEEDED,
    label: "Follow-up Needed",
    color: "orange",
  },
  { value: InteractionOutcome.COMPLETED, label: "Completed", color: "green" },
  { value: InteractionOutcome.DECLINED, label: "Declined", color: "red" },
  { value: InteractionOutcome.OTHER, label: "Other", color: "gray" },
];

// Interaction purposes (why)
export const INTERACTION_PURPOSES = [
  { value: "meeting", label: "Meeting", icon: "users" },
  { value: "review", label: "Review", icon: "document-check" },
  { value: "check-in", label: "Check-in", icon: "clipboard-check" },
  { value: "follow-up", label: "Follow-up", icon: "arrow-path" },
  { value: "other", label: "Other", icon: "ellipsis-horizontal" },
];

// Interaction methods (how)
export const INTERACTION_METHODS = [
  { value: "phone_call", label: "Phone Call", icon: "phone" },
  { value: "meeting", label: "Meeting", icon: "users" },
  { value: "email", label: "Email", icon: "mail" },
  { value: "in_person", label: "In-Person", icon: "user" },
  { value: "event", label: "Event", icon: "calendar" },
];

// Contact sources
export const CONTACT_SOURCES = [
  { value: "manual", label: "Manual Entry" },
  { value: "import", label: "Import" },
  { value: "qr_code", label: "QR Code" },
  { value: "event", label: "Event" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "api", label: "API" },
  { value: "other", label: "Other" },
];

// Contact lifecycle stages
export const LIFECYCLE_STAGES = [
  { value: "lead", label: "Lead", color: "yellow" },
  { value: "prospect", label: "Prospect", color: "blue" },
  { value: "customer", label: "Customer", color: "green" },
  { value: "partner", label: "Partner", color: "purple" },
  { value: "inactive", label: "Inactive", color: "gray" },
];

// Organization types
export const ORGANIZATION_TYPES = [
  { value: "headquarters", label: "Headquarters" },
  { value: "branch", label: "Branch" },
  { value: "franchise", label: "Franchise" },
  { value: "partner", label: "Partner" },
  { value: "client", label: "Client" },
  { value: "promoter", label: "Promoter" },
];

// Location types
export const LOCATION_TYPES = [
  { value: "office", label: "Office" },
  { value: "warehouse", label: "Warehouse" },
  { value: "retail", label: "Retail" },
  { value: "remote", label: "Remote" },
  { value: "other", label: "Other" },
];

// Document categories
export const DOCUMENT_CATEGORIES = [
  { value: "contract", label: "Contract" },
  { value: "invoice", label: "Invoice" },
  { value: "receipt", label: "Receipt" },
  { value: "proposal", label: "Proposal" },
  { value: "report", label: "Report" },
  { value: "presentation", label: "Presentation" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
  { value: "spreadsheet", label: "Spreadsheet" },
  { value: "pdf", label: "PDF" },
  { value: "other", label: "Other" },
];

// QR code purposes
export const QR_CODE_PURPOSES = [
  { value: "event_checkin", label: "Event Check-in" },
  { value: "contact_share", label: "Contact Share" },
  { value: "lead_capture", label: "Lead Capture" },
  { value: "document_access", label: "Document Access" },
  { value: "location_info", label: "Location Info" },
  { value: "marketing", label: "Marketing" },
  { value: "payment", label: "Payment" },
  { value: "feedback", label: "Feedback" },
  { value: "other", label: "Other" },
];

// Date format options
export const DATE_FORMATS = [
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (EU)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
  { value: "MMM DD, YYYY", label: "Jan 01, 2024" },
];

// Timezone options (common US timezones)
export const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona Time (MST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
];

// Currency options
export const CURRENCY_OPTIONS = [
  { value: "USD", label: "US Dollar ($)", symbol: "$" },
  { value: "EUR", label: "Euro (€)", symbol: "€" },
  { value: "GBP", label: "British Pound (£)", symbol: "£" },
  { value: "CAD", label: "Canadian Dollar (C$)", symbol: "C$" },
  { value: "MXN", label: "Mexican Peso (MX$)", symbol: "MX$" },
];

// Navigation menu items
// Dashboard is pinned at the top; everything below is alphabetical by `name`.
// Keep that contract intact when adding new items.
//
// OI-05 (2026-06-20): a nav item may now be a GROUP — a `children` array renders
// a collapsible parent in the sidebar (e.g. "Sales Team"). A group has no `path`.
// `placeholder: true` marks a not-yet-built child rendered disabled ("Soon").
export interface NavigationItem {
  name: string;
  path?: string;
  icon?: string;
  permission?: null;
  requiredRole?: string | null;
  requiredRoles?: string[];
  featureFlag?: string;
  children?: NavigationItem[];
  placeholder?: boolean;
}

export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: "home",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Account Reviews",
    path: "/account-reviews",
    icon: "clipboard-document-check",
    permission: null,
    requiredRole: null,
    featureFlag: "salesTeamManagement",
  },
  // NOTE: "Booth Check-in" (/booth-checkin) and "Event Check-in" (/event-field-checkin)
  // nav links were removed 2026-08 — both required login and were confusing operators
  // (an event routed attendees to the logged-in check-in page by mistake). Public
  // ticket-holder/walk-in check-in lives at /self-checkin; assisted per-event check-in
  // lives on each event's Operations tab. The routes still exist (unlinked, not deleted).
  {
    name: "Calendar Requests",
    path: "/calendar-requests",
    icon: "calendar",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Campaigns",
    path: "/campaigns",
    icon: "megaphone",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Contacts",
    path: "/contacts",
    icon: "users",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Content",
    path: "/content",
    icon: "document-duplicate",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Issue a Reward",
    path: "/rewards/issue",
    icon: "banknotes",
    permission: null,
    requiredRoles: ["super_admin", "admin"],
  },
  {
    name: "Event Performance",
    path: "/event-performance",
    icon: "chart-bar",
    permission: null,
    requiredRoles: ["super_admin", "admin"],
  },
  {
    // Restored to a top-level nav item 2026-06-23 (per operator request) — it had
    // been nested under the "Sales Team" group by OI-05; moved back to the main list.
    name: "Events",
    path: "/events",
    icon: "calendar",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Form Handlers",
    path: "/form-handlers",
    icon: "clipboard-document-list",
    permission: null,
    requiredRole: null,
    // Match the route's allowedRoles so the link never leads to Access Denied (2026-09-30).
    requiredRoles: ["super_admin", "admin"],
    featureFlag: "formHandlers",
  },
  {
    // Attorney question routing slice 2. requiredRoles MIRRORS the route's
    // allowedRoles in router.tsx so the link can never appear for a role the
    // route then denies, and the flag keeps it hidden in PROD until slices 3-4.
    name: "Attorney Questions",
    path: "/attorney-questions",
    icon: "scale",
    permission: null,
    requiredRoles: [
      "super_admin",
      "admin",
      "manager",
      "support_admin",
      "support_agent",
    ],
    featureFlag: "attorneyQuestionRouting",
  },
  {
    // Owner decision 2026-09-20: this page DOES get a nav link, unlike every other
    // /admin page in this sidebar. Roles mirror the route (managers read, admins
    // write — the RPCs enforce the write side server-side regardless of this).
    name: "Attorney Coverage",
    path: "/admin/attorney-coverage",
    icon: "scale",
    permission: null,
    requiredRoles: ["super_admin", "admin", "manager"],
    featureFlag: "attorneyQuestionRouting",
  },
  {
    name: "Interactions",
    path: "/interactions",
    icon: "chat-bubble-left-right",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Landing Pages",
    path: "/landing-pages",
    icon: "document-text",
    permission: null,
    requiredRole: null,
  },
  {
    // Sidebar uses exact-path matching, so the Lookups entry is its own
    // top-level item. Router-level RequireRole restricts access to
    // admin/superadmin/manager.
    name: "Lookups",
    path: "/campaigns/lookups",
    icon: "table-cells",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Offers",
    path: "/offers",
    icon: "gift",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Operations",
    path: "/error-logs",
    icon: "exclamation-triangle",
    permission: null,
    requiredRole: "super_admin", // Only superadmins can see operations dashboard
  },
  {
    name: "Organizations",
    path: "/organizations",
    icon: "building-office",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Partner Offers",
    path: "/partner-offers",
    icon: "gift",
    permission: null,
    requiredRole: null,
    featureFlag: "partnerOffersDashboard", // QA-only feature
  },
  {
    name: "Partners",
    path: "/partners",
    icon: "user-group",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Redemptions",
    path: "/redemptions",
    icon: "check-circle",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Reports",
    path: "/reports",
    icon: "chart-bar",
    permission: null,
    requiredRole: null,
  },
  {
    // OI-05 (2026-06-20): "Sales Team" sidebar grouping (order confirmed by
    // Heather). The group is a collapsible parent with no path of its own; each
    // child keeps its prior role/feature gating so visibility is unchanged from
    // when these were flat top-level items. Children that were flat before:
    // Non-Event Sales, Sales Calendar, Commission Estimator, and the hierarchy
    // page (relabeled "Sales Team Hierarchy"). MSR Availability Calendar is newly
    // surfaced here. (Events was moved back to the top-level list 2026-06-23.) Two commission entries are listed in
    // Heather's order; "Sales Earned Commissions" is a placeholder (blocked on
    // the commission engine, OI-01).
    name: "Sales Team",
    icon: "user-group",
    permission: null,
    requiredRole: null,
    children: [
      {
        // Renamed 2026-05-29 per Heather; URL + DB table names stay at
        // sales-activities to avoid churn (purely a label).
        name: "Non-Event Sales",
        path: "/sales-activities/phone-sales",
        icon: "phone",
        permission: null,
        requiredRole: null,
      },
      {
        name: "Sales Calendar",
        path: "/sales-calendar",
        icon: "calendar-days",
        permission: null,
        requiredRole: null,
        featureFlag: "salesCalendar",
      },
      {
        // Business lead 2026-10-27: managers see the MSRs' availability (team calendar or one rep), their verified
        // sales and typed-in reinstatements/upgrades. Replaces the link to the rep's own availability page.
        name: "MSR Team",
        path: "/msr-team",
        icon: "calendar-days",
        permission: null,
        requiredRole: null,
        requiredRoles: [
          "super_admin",
          "admin",
          "manager",
          "regional_manager",
          "territory_manager",
          "staff",
        ],
      },
      {
        name: "Sales Team Hierarchy",
        path: "/sales-team",
        icon: "chart-bar",
        permission: null,
        requiredRole: null,
        featureFlag: "salesTeamManagement",
      },
      {
        // Placeholder — the actual earned-commission report is blocked on the
        // commission engine sign-off (OI-01). Rendered disabled ("Soon").
        name: "Sales Earned Commissions",
        icon: "chart-bar",
        permission: null,
        placeholder: true,
        requiredRoles: [
          "super_admin",
          "admin",
          "manager",
          "regional_manager",
          "territory_manager",
          "staff",
        ],
        featureFlag: "salesTeamManagement",
      },
      {
        // SA-6: the live MSR commission estimator (Heather's "MSR Monthly
        // Earnings Commission Estimator"). Kept linked — it ships today.
        name: "Commission Estimator",
        path: "/commission-estimator",
        icon: "chart-bar",
        permission: null,
        requiredRole: null,
        requiredRoles: [
          "super_admin",
          "admin",
          "manager",
          "regional_manager",
          "territory_manager",
          "staff",
          "msr",
        ],
        featureFlag: "salesTeamManagement",
      },
    ],
  },
  {
    name: "Segments",
    path: "/segments",
    icon: "list-bullet",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Sequences",
    path: "/sequences",
    icon: "bolt",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Settings",
    path: "/settings",
    icon: "cog-6-tooth",
    permission: null,
    requiredRole: null,
  },
  {
    name: "Support Tasks",
    path: "/support-tasks",
    icon: "phone",
    permission: null,
    requiredRole: null,
    // Match the route's allowedRoles; managers removed 2026-09-30 (owner).
    requiredRoles: [
      "super_admin",
      "admin",
      "staff",
      "support_admin",
      "support_agent",
    ],
    featureFlag: "supportTasksDashboard", // QA-only feature
  },
  {
    name: "Tasks",
    path: "/tasks",
    icon: "clipboard-document-list",
    permission: null,
    requiredRole: null,
  },
  {
    // In-app release notes. Last in the list, which is also where it falls
    // alphabetically — this list is kept in alphabetical order.
    //
    // No requiredRoles: this list is only reached by roles that already fall
    // through to it (super_admin/admin/manager/staff/readonly/it/concierge).
    // partner, finance, msr and the two support roles get hard-coded sidebars
    // in Sidebar.tsx instead; the support pair is handled there, and partner is
    // refused the data by the database in any case.
    name: "What's New",
    path: "/whats-new",
    icon: "sparkles",
    permission: null,
    requiredRole: null,
  },
];

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: "Network error. Please check your connection and try again.",
  UNAUTHORIZED: "You are not authorized to perform this action.",
  NOT_FOUND: "The requested resource was not found.",
  SERVER_ERROR: "An unexpected error occurred. Please try again later.",
  VALIDATION_ERROR: "Please check your input and try again.",
  UPLOAD_SIZE_ERROR: `File size exceeds maximum allowed size of ${MAX_UPLOAD_SIZE / 1024 / 1024}MB.`,
  UPLOAD_TYPE_ERROR: "File type not allowed.",
};

// Success messages
export const SUCCESS_MESSAGES = {
  CREATED: "Created successfully",
  UPDATED: "Updated successfully",
  DELETED: "Deleted successfully",
  UPLOADED: "Uploaded successfully",
  IMPORTED: "Imported successfully",
  EXPORTED: "Exported successfully",
};

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  USER_PREFERENCES: "user_preferences",
  THEME: "theme",
  SIDEBAR_COLLAPSED: "sidebar_collapsed",
};

// Query keys for React Query
export const QUERY_KEYS = {
  PROFILE: "profile",
  ORGANIZATIONS: "organizations",
  LOCATIONS: "locations",
  CONTACTS: "contacts",
  TASKS: "tasks",
  EVENTS: "events",
  INTERACTIONS: "interactions",
  DOCUMENTS: "documents",
  QR_CODES: "qr_codes",
  CONTENT: "content",
  CONTENT_ITEM: "content_item",
  LISTS: "lists",
  SEGMENTS: "segments",
  CALENDAR_REQUESTS: "calendar-requests",
  SYSTEM_ALERTS: "system_alerts",
  ALERT_RULES: "alert_rules",
};
